import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const MAX_RETRIES = 2
const LOAD_TIMEOUT_MS = 6000
const RETRY_DELAYS_MS = [400, 900] as const

const FALLBACK_PALETTES = [
  {
    background:
      'linear-gradient(135deg, #111827 0%, #334155 50%, #f59e0b 100%)',
    accentA: 'rgba(255, 255, 255, 0.24)',
    accentB: 'rgba(251, 191, 36, 0.28)',
  },
  {
    background:
      'linear-gradient(135deg, #172554 0%, #1d4ed8 48%, #93c5fd 100%)',
    accentA: 'rgba(255, 255, 255, 0.18)',
    accentB: 'rgba(191, 219, 254, 0.34)',
  },
  {
    background:
      'linear-gradient(135deg, #1f2937 0%, #374151 45%, #fb7185 100%)',
    accentA: 'rgba(255, 255, 255, 0.2)',
    accentB: 'rgba(251, 113, 133, 0.24)',
  },
  {
    background:
      'linear-gradient(135deg, #0f172a 0%, #155e75 50%, #67e8f9 100%)',
    accentA: 'rgba(255, 255, 255, 0.2)',
    accentB: 'rgba(103, 232, 249, 0.28)',
  },
  {
    background:
      'linear-gradient(135deg, #1c1917 0%, #57534e 48%, #f97316 100%)',
    accentA: 'rgba(255, 255, 255, 0.16)',
    accentB: 'rgba(251, 146, 60, 0.28)',
  },
] as const

function hashString(value: string) {
  let hash = 0

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index)
    hash |= 0
  }

  return Math.abs(hash)
}

function getRetrySource(src: string, attempt: number, retryToken: number) {
  if (attempt === 0 || src === '') return src

  const hashIndex = src.indexOf('#')
  const hash = hashIndex >= 0 ? src.slice(hashIndex) : ''
  const sourceWithoutHash = hashIndex >= 0 ? src.slice(0, hashIndex) : src
  const separator = sourceWithoutHash.includes('?') ? '&' : '?'

  return `${sourceWithoutHash}${separator}ui_image_retry=${attempt}-${retryToken}${hash}`
}

interface ImageWithFallbackProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'onLoad' | 'onError'> {
  src: string;
  alt: string;
  fallbackClassName?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: () => void;
}

export function ImageWithFallback({ 
  src, 
  alt, 
  style, 
  className, 
  fallbackClassName,
  onLoad,
  onError,
  sizes,
  loading = 'lazy',
  decoding = 'async',
  ...rest 
}: ImageWithFallbackProps) {
  const [retryState, setRetryState] = useState(() => ({ src, attempt: 0, token: Date.now() }))
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  const [failedSrc, setFailedSrc] = useState<string | null>(src === '' ? src : null)
  const [monitoredSrc, setMonitoredSrc] = useState<string | null>(loading === 'eager' ? src : null)
  const imageRef = useRef<HTMLImageElement>(null)
  const timeoutRef = useRef<number | null>(null)
  const retryRef = useRef<number | null>(null)
  const failureHandledRef = useRef(false)
  const attempt = retryState.src === src ? retryState.attempt : 0
  const retryToken = retryState.src === src ? retryState.token : 0
  const didError = src === '' || failedSrc === src
  const isLoaded = loadedSrc === src
  const shouldMonitorTimeout = loading === 'eager' || monitoredSrc === src
  const requestedSrc = useMemo(
    () => getRetrySource(src, attempt, retryToken),
    [attempt, retryToken, src],
  )
  const palette = useMemo(() => {
    const seed = `${src}:${alt}`
    return FALLBACK_PALETTES[hashString(seed) % FALLBACK_PALETTES.length]
  }, [alt, src])

  const clearTimers = useCallback(() => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
    if (retryRef.current !== null) window.clearTimeout(retryRef.current)
    timeoutRef.current = null
    retryRef.current = null
  }, [])

  useEffect(() => {
    clearTimers()
    failureHandledRef.current = false
  }, [clearTimers, src])

  useEffect(() => {
    if (loading === 'eager' || shouldMonitorTimeout) return

    const image = imageRef.current
    if (!image || typeof IntersectionObserver === 'undefined') {
      setMonitoredSrc(src)
      return
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setMonitoredSrc(src)
        observer.disconnect()
      }
    }, { rootMargin: '300px' })

    observer.observe(image)
    return () => observer.disconnect()
  }, [loading, shouldMonitorTimeout, src])

  const handleAttemptFailure = useCallback(() => {
    if (failureHandledRef.current || didError) return

    failureHandledRef.current = true
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)

    if (attempt < MAX_RETRIES) {
      retryRef.current = window.setTimeout(() => {
        setRetryState({ src, attempt: attempt + 1, token: Date.now() })
      }, RETRY_DELAYS_MS[attempt])
      return
    }

    setFailedSrc(src)
    onError?.()
  }, [attempt, didError, onError, src])

  useEffect(() => {
    if (didError || isLoaded || requestedSrc === '' || !shouldMonitorTimeout) return

    failureHandledRef.current = false
    timeoutRef.current = window.setTimeout(handleAttemptFailure, LOAD_TIMEOUT_MS)

    return () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [attempt, didError, handleAttemptFailure, isLoaded, requestedSrc, shouldMonitorTimeout])

  useEffect(() => clearTimers, [clearTimers])

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    clearTimers()
    failureHandledRef.current = true
    setLoadedSrc(src)
    setFailedSrc((currentSrc) => currentSrc === src ? null : currentSrc)
    onLoad?.(e)
  }

  const handleError = () => handleAttemptFailure()

  if (didError) {
    return (
      <div
        className={`relative inline-flex h-full w-full overflow-hidden ${fallbackClassName || ''}`}
        style={{
          backgroundImage: palette.background,
          backgroundColor: '#1f2937',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        role="img"
        aria-label={alt}
      >
        <div
          className="absolute inset-0 opacity-80"
          style={{
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(15,23,42,0.18) 100%)',
          }}
        />
        <div
          className="absolute -left-[14%] top-[12%] h-[45%] w-[45%] rounded-full blur-3xl"
          style={{ backgroundColor: palette.accentA }}
        />
        <div
          className="absolute -right-[12%] bottom-[10%] h-[50%] w-[50%] rounded-full blur-3xl"
          style={{ backgroundColor: palette.accentB }}
        />
        <div className="absolute inset-0 border border-white/10" />
      </div>
    )
  }

  return (
    <div className={`relative inline-flex h-full w-full items-center justify-center overflow-hidden ${fallbackClassName || ''}`}>
      {!isLoaded && (
        <div
          aria-hidden="true"
          className="absolute inset-0 animate-pulse"
          style={{
            backgroundImage: palette.background,
            backgroundColor: '#1f2937',
            backgroundSize: 'cover',
            opacity: 0.72,
          }}
        />
      )}

      <img
        key={attempt}
        ref={imageRef}
        src={requestedSrc}
        alt={alt}
        className={`relative z-10 ${className || ''} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        style={style}
        onLoad={handleLoad}
        onError={handleError}
        sizes={sizes}
        loading={attempt > 0 ? 'eager' : loading}
        decoding={decoding}
        {...rest}
      />
    </div>
  )
}
